import { Link } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import { useSEO } from '../../utils/seo';

export default function AboutPage() {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://www.masdeuna.org';
  
  useSEO({
    title: 'About Our Founder - Adaluz Julie Veloz | MasDeUna',
    description: 'Meet Adaluz Julie Veloz, founder of MasDeUna. A first-generation Latina leader, entrepreneur, and advocate for diversity in leadership with over 20 years of experience creating pathways for underrepresented communities.',
    keywords: 'Adaluz Julie Veloz, Latina leadership, founder, women empowerment, diversity advocate, MasDeUna',
    canonical: `${siteUrl}/about`,
    schema: {
      '@context': 'https://schema.org',
      '@type': 'Person',
      name: 'Adaluz Julie Veloz',
      alternateName: 'Julie Veloz',
      url: 'https://www.julieveloz.com',
      image: 'https://static.readdy.ai/image/ddb3f6bea97a6774f2df2489e0165868/cac72a81f079383ba39d558f6fcfe214.jpeg',
      jobTitle: 'Founder',
      worksFor: {
        '@type': 'Organization',
        name: 'MasDeUna',
        url: siteUrl
      },
      description: 'First-generation Latina leader, entrepreneur, and advocate for diversity in leadership with over 20 years of experience in corporate America and the nonprofit sector.',
      sameAs: [
        'https://www.julieveloz.com'
      ]
    }
  });

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 bg-gradient-to-br from-[#111111] to-[#2a2a2a]">
        <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/20 to-black/30"></div>
        <div className="container mx-auto px-6 relative z-10">
          
          <h1 className="font-['Poppins'] font-bold text-6xl md:text-7xl lg:text-8xl text-white mb-6 text-center">
            Our Founder
          </h1>
          <p className="font-['Inter'] text-xl md:text-2xl text-white/90 text-center max-w-3xl mx-auto">
            Meet the visionary behind MasDeUna
          </p>
        </div>
      </section>

      {/* Founder Bio Section */}
      <section className="py-20">
        <div className="container mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center max-w-7xl mx-auto">
            {/* Image */}
            <div className="order-2 lg:order-1">
              <a 
                href="https://www.julieveloz.com" 
                target="_blank" 
                rel="noopener noreferrer"
                className="block cursor-pointer"
              >
                <div className="relative w-full h-[600px] rounded-2xl overflow-hidden shadow-2xl hover:shadow-3xl transition-shadow duration-300">
                  <img 
                    src="https://static.readdy.ai/image/ddb3f6bea97a6774f2df2489e0165868/cac72a81f079383ba39d558f6fcfe214.jpeg"
                    alt="Julie Chavez - Founder of MasDeUna"
                    className="w-full h-full object-cover"
                    style={{ objectPosition: '50% 35%' }}
                  />
                </div>
              </a>
              
              {/* Logo under picture */}
              <div className="flex justify-center mt-8">
                <img 
                  src="https://static.readdy.ai/image/ddb3f6bea97a6774f2df2489e0165868/942f95f8471d07d7cc1f0274fe5c1d76.png"
                  alt="MasDeUna Logo"
                  className="h-48 w-auto object-contain"
                />
              </div>
            </div>

            {/* Bio Content */}
            <div className="order-1 lg:order-2 space-y-6">
              <div className="mb-8">
                <h2 className="font-['Poppins'] font-bold text-4xl md:text-5xl text-[#111111] mb-3">
                  Adaluz 'Julie' Veloz
                </h2>
                <p className="font-['Inter'] text-xl text-gray-600 mb-2">
                  Founder of MasDeUna
                </p>
                <a 
                  href="https://www.julieveloz.com" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="font-['Inter'] text-lg text-[#E6007E] hover:text-[#c00069] transition-colors cursor-pointer"
                >
                  www.julieveloz.com
                </a>
              </div>
              
              <div className="space-y-6 font-['Inter'] text-lg text-gray-700 leading-relaxed">
                <p>
                  Adaluz 'Julie' Veloz is a first-generation Latina leader, entrepreneur, and advocate for diversity in leadership. With over 20 years of experience in corporate America and the nonprofit sector, Julie has dedicated her career to creating pathways for underrepresented communities.
                </p>
                
                <p>
                  Born and raised in a working-class family, Julie understands firsthand the challenges that Latinas face in accessing mentorship, professional development, and leadership opportunities. Her journey from entry-level positions to executive leadership inspired her to create MasDeUna.
                </p>
                
                <p>
                  "I remember feeling isolated and unsure of how to navigate my career," Julie reflects. "I didn't have anyone who looked like me in leadership positions to guide me. I created MasDeUna to ensure that no Latina has to feel that way again."
                </p>
                
                <p>
                  What started as a grassroots initiative during Hispanic Heritage Month has grown into a year-round movement connecting hundreds of Latinas across industries. Julie's vision is simple yet powerful: one 60-minute cafecito chat can transform the trajectory of a rising Latina's career.
                </p>
                
                <p>
                  Through MasDeUna, Julie has built a community where senior Latina leaders invest their time and wisdom into the next generation, creating a ripple effect of empowerment and success.
                </p>
                
                <p className="font-semibold text-[#E6007E] text-xl">
                  "We are stronger together. When one Latina rises, we all rise."
                </p>
              </div>

              {/* CTA */}
              <div className="pt-8">
                <Link
                  to="/join"
                  className="inline-block px-8 py-4 bg-[#E6007E] text-white font-['Poppins'] font-semibold text-lg rounded-lg hover:bg-[#c00069] transition-all duration-300 shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer"
                >
                  Join the Movement
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Mission Statement */}
      <section className="py-20 bg-[#FFC72C]/10">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <h3 className="font-['Poppins'] font-bold text-3xl md:text-4xl text-[#111111] mb-8">
              La Mision
            </h3>
            <p className="font-['Inter'] text-xl text-gray-700 leading-relaxed mb-8">
              Rooted in the belief that we are stronger together, we foster a community where Latinas can support one another, ensuring that every woman has the opportunity to thrive and build a lasting legacy of success for those who follow.
            </p>
            <p className="font-['Inter'] text-xl text-gray-700 leading-relaxed">
              Being part of MASDEUNA is a simple commitment of one 60-minute cafecito chat with an emerging Latina talent. Helping guide the path of their career, carrying your legacy torch of service with them along the way.
            </p>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
